import dedent from 'dedent'
import { existsSync } from 'fs'
import fs from 'fs/promises'
import path from 'path'
import { AgentProject } from '../agent-project/index.js'
import { generateIntegrationTypes } from '../generators/integration-types.js'
import { generateInterfaceTypes } from '../generators/interface-types.js'
import { generateTableTypes } from '../generators/table-types.js'
import { generateTriggerTypes } from '../generators/trigger-types.js'
import { generateStateTypes } from '../generators/state-types.js'
import { generateWorkflowTypes } from '../generators/workflow-types.js'
import { generateConversationTypes } from '../generators/conversation-types.js'
import { generateEventTypes } from '../generators/event-types.js'
import { IntegrationManager } from '../integrations/manager.js'
import { InterfaceManager } from '../interfaces/manager.js'
import { createFile } from '../utils/fs.js'
import { pascalCase } from '../utils/strings.js'
import { DevIdManager } from './dev-id-manager.js'
import { IntegrationSync } from './integration-sync.js'
import { InterfaceSync } from './interface-sync.js'
import { formatCode, ADK_VERSION } from '../generators/utils.js'
import { transforms, z } from '@botpress/sdk'
import { getIntegrationAlias } from '../utils/ids.js'
import { BUILTIN_INTERFACES } from '../constants.js'
import { BuiltInActions, BuiltInWorkflows, Primitives, Workflow } from '@botpress/runtime/internal'

export interface BotGeneratorOptions {
  projectPath: string
  outputPath?: string
  adkCommand?: 'adk-dev' | 'adk-build' | 'adk-deploy'
}

function isBuiltinWorkflow(name: string): boolean {
  return !!Object.values(BuiltInWorkflows).find((x) => x.name === name)
}

function isBuiltinAction(name: string): boolean {
  return !!Object.values(BuiltInActions).find((x) => x.name === name)
}

/**
 * Generate a normalized import path for use in import statements
 * Converts Windows backslashes to forward slashes and removes .ts extension
 */
function getImportPath(from: string, to: string): string {
  return path.relative(path.dirname(from), to).replace(/\.ts$/, '').replace(/\\/g, '/')
}

export class BotGenerator {
  private projectPath: string
  private outputPath: string

  constructor(options: BotGeneratorOptions) {
    this.projectPath = path.resolve(options.projectPath)
    this.outputPath = path.resolve(options.outputPath || path.join(this.projectPath, '.adk'))
  }

  private async listFilesRecursive(rootDir: string): Promise<string[]> {
    try {
      if (!existsSync(rootDir)) return []
      const result: string[] = []
      const walk = async (dir: string, relativeBase: string) => {
        const entries = await fs.readdir(dir, { withFileTypes: true })
        for (const entry of entries) {
          const abs = path.join(dir, entry.name)
          const rel = path.join(relativeBase, entry.name)
          if (entry.isDirectory()) {
            await walk(abs, rel)
          } else {
            result.push(rel)
          }
        }
      }
      await walk(rootDir, '')
      return result.sort()
    } catch {
      return []
    }
  }

  private async removeEmptyDirectories(rootDir: string): Promise<void> {
    if (!existsSync(rootDir)) return
    const removeIfEmpty = async (dir: string) => {
      const entries = await fs.readdir(dir, { withFileTypes: true })
      for (const entry of entries) {
        if (entry.isDirectory()) {
          const subdir = path.join(dir, entry.name)
          await removeIfEmpty(subdir)
        }
      }

      // Re-read after potential removals
      const after = await fs.readdir(dir)
      if (after.length === 0 && dir !== rootDir) {
        try {
          await fs.rmdir(dir, { recursive: false })
        } catch {}
      }
    }
    await removeIfEmpty(rootDir)
  }

  async generate(): Promise<void> {
    // Load the agent project
    const project = await AgentProject.load(this.projectPath)

    // Create output directory
    await fs.mkdir(this.outputPath, { recursive: true })

    // Generate bot files
    await this.generateBotDefinition()
    await this.generateIntegrationsDefinition()
    await this.generateInterfacesDefinition()
    await this.generateIntegrationsTypes()
    await this.generateInterfacesTypes()
    await this.generateTableTypes()
    await this.generateTriggerTypes()
    await this.generateStateTypes()
    await this.generateWorkflowTypes()
    await this.generateConversationTypes()
    await this.generateActionTypes()
    await this.generateEventTypes()
    await this.generateIntegrationActionTypes()
    await this.generateRuntimeTypes()
    await this.generateBotIndex()
    await this.generatePackageJson(project)
    await this.generateTsConfig()
    await this.generateGlobalTypes()
    await this.copyAssets()
  }

  private async generateIntegrationsTypes(): Promise<void> {
    // Load the project to get interfaces
    const project = await AgentProject.load(this.projectPath)
    const manager = new IntegrationManager({
      workspaceId: project.agentInfo?.workspaceId,
    })
    const integrations = await manager.loadIntegrations(project.dependencies || {})

    // List existing files before generation (.adk/integrations)
    const integrationsDir = path.join(this.projectPath, '.adk', 'integrations')
    const existingIntegrationFiles = await this.listFilesRecursive(integrationsDir)

    let aliases = new Set<string>()
    let files = new Set<string>() // tracks all files created in this run (relative to .adk/integrations)

    // Generate imports for each integration
    for (const integration of integrations.integrations) {
      if (integration.enabled && integration.definition) {
        const types = await generateIntegrationTypes(integration)

        // Use dynamic import for type
        const importPath = `./${path.join('integrations', types.names.paths.index).replace(/\\/g, '/')}`
        aliases.add(`"${integration.alias}": import("${importPath}").${types.names.typings.index}`)

        for (const [filePath, content] of Object.entries(types.files)) {
          const fullPath = path.join(this.projectPath, '.adk', 'integrations', filePath)

          const dir = path.dirname(fullPath)
          await fs.mkdir(dir, { recursive: true })
          await createFile(fullPath, content)
          files.add(filePath)
        }
      }
    }

    const types = `
      ////////////////////////////////////////////////////////
      // DO NOT EDIT THIS FILE DIRECTLY
      // This file is auto-generated from the Botpress ADK
      // File: integrations.ts
      ////////////////////////////////////////////////////////

      declare module "@botpress/runtime/_types/integrations" {
        export type Integrations = {
          ${Array.from(aliases).join(`, `)}
        };
      }
      `

    await createFile(path.join(this.projectPath, '.adk', 'integrations.d.ts'), await formatCode(types))

    // Cleanup stale integration files
    const staleIntegrationFiles = existingIntegrationFiles.filter((f) => !files.has(f))
    if (staleIntegrationFiles.length > 0) {
      for (const rel of staleIntegrationFiles) {
        const abs = path.join(integrationsDir, rel)
        try {
          await fs.rm(abs, { force: true })
        } catch {}
      }
    }
    // remove empty folders after deletion
    await this.removeEmptyDirectories(integrationsDir)
  }

  private async generateTableTypes(): Promise<void> {
    const project = await AgentProject.load(this.projectPath)
    await generateTableTypes(project)
  }

  private async generateTriggerTypes(): Promise<void> {
    const project = await AgentProject.load(this.projectPath)
    await generateTriggerTypes(project)
  }

  private async generateStateTypes(): Promise<void> {
    const project = await AgentProject.load(this.projectPath)
    await generateStateTypes(project)
  }

  private async generateEventTypes(): Promise<void> {
    const project = await AgentProject.load(this.projectPath)
    await generateEventTypes(project)
  }

  private async generateWorkflowTypes(): Promise<void> {
    const project = await AgentProject.load(this.projectPath)
    await generateWorkflowTypes(project)
  }

  private async generateConversationTypes(): Promise<void> {
    const project = await AgentProject.load(this.projectPath)
    await generateConversationTypes(project)
  }

  private async generateActionTypes(): Promise<void> {
    const project = await AgentProject.load(this.projectPath)
    const { generateActionTypes } = await import('../generators/action-types.js')
    await generateActionTypes(project)
  }

  private async generateIntegrationActionTypes(): Promise<void> {
    const project = await AgentProject.load(this.projectPath)
    const { generateIntegrationActionTypes } = await import('../generators/integration-action-types.js')
    await generateIntegrationActionTypes(project)
  }

  private async generateRuntimeTypes(): Promise<void> {
    // Load the project for conditional imports
    const project = await AgentProject.load(this.projectPath)

    // Load integrations to generate channel types
    const manager = new IntegrationManager({
      workspaceId: project.agentInfo?.workspaceId,
    })
    const integrations = await manager.loadIntegrations(project.dependencies || {})

    // Collect all channel names from integrations
    const channels: string[] = []
    for (const integration of integrations.integrations) {
      if (integration.enabled && integration.definition) {
        const alias = integration.alias
        for (const channelName of Object.keys(integration.definition.channels || {})) {
          channels.push(`"${alias}.${channelName}"`)
        }
      }
    }

    // Generate channel union type
    const channelsType = channels.length > 0 ? channels.join(' | ') : 'never'

    // Get state types from agent config
    let botStateType = '{}'
    let userStateType = '{}'

    try {
      const configPath = path.join(project.path, 'agent.config.ts')
      // Bust module cache to ensure fresh config on regeneration
      const configModule = await import(`${configPath}?t=${Date.now()}`)
      const config = configModule.default

      if (config?.bot?.state) {
        const botSchema = config.bot.state as z.ZodTypeAny
        if (botSchema.toTypescriptType) {
          botStateType = botSchema.toTypescriptType()
        }
      }

      if (config?.user?.state) {
        const userSchema = config.user.state as z.ZodTypeAny
        if (userSchema.toTypescriptType) {
          userStateType = userSchema.toTypescriptType()
        }
      }
    } catch (error) {
      console.warn('Failed to load agent config for state types:', error)
    }

    const types = `
////////////////////////////////////////////////////////
// DO NOT EDIT THIS FILE DIRECTLY
// This file is auto-generated from the Botpress ADK
// ADK Version: ${ADK_VERSION}
// Generated at: ${new Date().toISOString()}
////////////////////////////////////////////////////////

declare module "@botpress/runtime/_types/channels" {
  export type Channels = ${channelsType};
  export type ChannelSpec = Channels | readonly Channels[] | '*';
}

declare module "@botpress/runtime/_types/state" {
  export type BotState = ${botStateType};
  export type UserState = ${userStateType};
}
`

    await createFile(path.join(this.projectPath, '.adk', 'runtime.d.ts'), await formatCode(types))
  }

  private async generateInterfacesTypes(): Promise<void> {
    // Load the project to get interfaces
    const project = await AgentProject.load(this.projectPath)
    const integrationManager = new IntegrationManager({
      workspaceId: project.agentInfo?.workspaceId,
    })
    const manager = new InterfaceManager()

    // List existing files before generation (.adk/interfaces)
    const interfacesDir = path.join(this.projectPath, '.adk', 'interfaces')
    const existingInterfaceFiles = await this.listFilesRecursive(interfacesDir)

    const interfaces = await manager
      .loadInterfaces(project.dependencies || {})
      .then((result) => result.interfaces.filter((int) => int.definition).map((x) => x.definition!))

    const integrations = await integrationManager
      .loadIntegrations(project.dependencies || {})
      .then((result) => result.integrations.filter((int) => int.enabled && int.definition).map((x) => x.definition!))

    let imports = new Set<string>()
    let aliases = new Set<string>()
    let files = new Set<string>() // tracks all files created in this run (relative to .adk/interfaces)

    // Generate imports for each integration
    for (const int of interfaces) {
      const types = await generateInterfaceTypes(int, integrations)

      imports.add(
        `import { ${types.names.typings.index} } from "./${path.join('interfaces', types.names.paths.index).replace(/\\/g, '/')}";`
      )

      aliases.add(`"${types.names.name}": ${types.names.typings.index}`)

      for (const [filePath, content] of Object.entries(types.files)) {
        const fullPath = path.join(this.projectPath, '.adk', 'interfaces', filePath)

        const dir = path.dirname(fullPath)
        await fs.mkdir(dir, { recursive: true })
        await createFile(fullPath, content)
        files.add(filePath)
      }
    }

    const types = `
      ////////////////////////////////////////////////////////
      // DO NOT EDIT THIS FILE DIRECTLY
      // This file is auto-generated from the Botpress ADK
      // File: interfaces.d.ts
      ////////////////////////////////////////////////////////

      ${Array.from(imports).join('\n')}

      declare global {
        export type Interfaces = {
          ${Array.from(aliases).join(`, `)}
        };
      }
      `

    const consts = `
      ////////////////////////////////////////////////////////
      // DO NOT EDIT THIS FILE DIRECTLY
      // This file is auto-generated from the Botpress ADK
      // File: interfaces.ts
      ////////////////////////////////////////////////////////

      ${Array.from(imports)
        .map((x) => x.replace('.ts"', '"'))
        .join('\n')}

      export const Interfaces = {
        ${Array.from(aliases).join(',\n')}
      };
      `

    await createFile(path.join(this.projectPath, '.adk', 'interfaces.d.ts'), await formatCode(types))

    await createFile(path.join(this.projectPath, '.adk', 'interfaces.ts'), await formatCode(consts))

    // Cleanup stale interface files
    const staleInterfaceFiles = existingInterfaceFiles.filter((f) => !files.has(f))
    if (staleInterfaceFiles.length > 0) {
      for (const rel of staleInterfaceFiles) {
        const abs = path.join(interfacesDir, rel)
        try {
          await fs.rm(abs, { force: true })
        } catch {}
      }
    }
    // remove empty folders after deletion
    await this.removeEmptyDirectories(interfacesDir)
  }

  private async generateIntegrationsDefinition(): Promise<void> {
    // Load the project to get integrations
    const project = await AgentProject.load(this.projectPath)
    const integrations = project.dependencies?.integrations || {}

    // Generate imports for each integration
    const imports: string[] = []
    const integrationDefs: string[] = []

    for (const alias of Object.keys(integrations).map(getIntegrationAlias)) {
      imports.push(`import integration_${alias} from "../bp_modules/integration_${alias}";`)
      integrationDefs.push(`${alias}: integration_${alias}`)
    }

    const content = dedent`
      import { IntegrationPackage } from "@botpress/sdk";

      ${imports.length > 0 ? '\n' + imports.join('\n') : ''}

      export const IntegrationDefinitions = {
       ${integrationDefs.length > 0 ? integrationDefs.join(',\n') : ''}
      } as Record<string, IntegrationPackage>;
    `

    await createFile(path.join(this.outputPath, 'src', 'integrations.ts'), content)
  }

  private async generateInterfacesDefinition(): Promise<void> {
    // Use hard-coded built-in interfaces
    const interfaces = BUILTIN_INTERFACES

    // Generate imports for each interface
    const imports: string[] = []
    const interfaceDefs: string[] = []

    for (const alias of Object.keys(interfaces)) {
      const pascalAlias = pascalCase(alias)
      imports.push(`import interface_${pascalAlias} from "../bp_modules/interface_${pascalAlias}";`)
      interfaceDefs.push(`${pascalAlias}: interface_${pascalAlias}`)
    }

    const content = dedent`
      import { InterfacePackage } from "@botpress/sdk";

      ${imports.length > 0 ? '\n' + imports.join('\n') : ''}

      export const InterfaceDefinitions = {
       ${interfaceDefs.length > 0 ? interfaceDefs.join(',\n') : ''}
      } as Record<string, InterfacePackage>;
    `

    await createFile(path.join(this.outputPath, 'src', 'interfaces.ts'), await formatCode(content))
  }

  private async generateBotDefinition(): Promise<void> {
    // Load the project to get integrations, interfaces, and triggers
    const project = await AgentProject.load(this.projectPath)
    const integrations = project.integrations

    // Generate imports for each integration and interface
    const imports: string[] = []
    const addIntegrations: string[] = []

    for (const { alias } of integrations.filter((i) => i.enabled)) {
      const importName = `integration_${getIntegrationAlias(alias)}`
      imports.push(`import ${importName} from "./bp_modules/${importName}";`)
      addIntegrations.push(`bot.addIntegration(${importName}, { alias: "${alias}" });`)
    }

    // Generate trigger tags for conversations
    const conversationTags: Record<string, any> = {}
    const workflowTags: Record<string, any> = {}

    // Helper to hash strings - creates short uppercase hashes
    const crypto = require('crypto')
    const hashString = (str: string) => {
      return crypto.createHash('md5').update(str).digest('hex').substring(0, 5).toUpperCase()
    }

    // For each trigger, we need to add possible tags
    for (const trigger of project.triggers) {
      const triggerName = trigger.definition.name

      // Add tag for this trigger - the tag name is based on trigger name
      // The value can be "*" for wildcard or a specific match result
      const tagName = `trigger${hashString(triggerName)}`
      conversationTags[tagName] = {
        title: `Trigger: ${triggerName}`,
        description: `Subscribe to events from trigger "${triggerName}" (use "*" for all events or specific values)`,
      }

      // TODO: Only add workflow tags when workflows exist
      // Currently we don't support workflow definitions yet
      // When implemented, check if project.workflows.length > 0
      // workflowTags[tagName] = {
      //   title: `Trigger: ${triggerName}`,
      //   description: `Subscribe to events from trigger "${triggerName}" (use "*" for all events or specific values)`
      // };
    }

    // Load workflow definitions at build time and generate their schemas
    const workflowDefs: string[] = []
    const recurringWorkflows: Array<{ name: string; schedule: string }> = []

    const workflowInstances: Workflow<any>[] = []

    for (const workflow of project.workflows) {
      try {
        if (isBuiltinWorkflow(workflow.definition.name)) {
          // Built-in workflow - already handled in runtime
          continue
        }

        // User-defined workflow - import from project
        const workflowPath = path.join(project.path, workflow.path)
        // Bust module cache to ensure fresh workflow on regeneration
        const workflowModule = await import(`${workflowPath}?t=${Date.now()}`)
        const workflowInstance = (workflowModule.default || workflowModule[workflow.export]) as Workflow | undefined

        if (workflowInstance) {
          workflowInstances.push(workflowInstance)
        }
      } catch (error) {
        console.error(`Failed to load workflow ${workflow.definition.name}:`, error)
      }
    }

    for (const workflow of Object.values(BuiltInWorkflows)) {
      workflowInstances.push(workflow)
    }

    for (const workflow of workflowInstances) {
      const definition = Primitives.Definitions.getDefinition(workflow)
      if (!Primitives.Definitions.isWorkflowDefinition(definition)) {
        continue
      }

      // Check if workflow has a schedule property for recurring execution
      if (definition.schedule) {
        recurringWorkflows.push({
          name: definition.name,
          schedule: definition.schedule,
        })
      }

      // Generate the workflow definition as inline code
      const inputSchema = definition.input
        ? transforms.fromJSONSchema(definition.input).naked().toTypescriptSchema()
        : 'z.object({})'

      const outputSchema = definition.output
        ? transforms.fromJSONSchema(definition.output).naked().toTypescriptSchema()
        : 'z.object({})'

      const parts = []
      if (definition.description) {
        parts.push(`title: ${JSON.stringify(definition.name)}`)
        parts.push(`description: ${JSON.stringify(definition.description)}`)
      }
      parts.push(`input: { schema: ${inputSchema} }`)
      parts.push(`output: { schema: ${outputSchema} }`)

      // Add the key tag for workflow discrimination
      parts.push(`tags: {
          key: {
            title: "Workflow Key",
            description: "Unique key for workflow deduplication"
          }
        }`)

      workflowDefs.push(`"${definition.name}": {\n          ${parts.join(',\n          ')}\n        }`)
    }

    const actionDefs: Record<string, any> = {}
    for (const action of project.actions) {
      const def = action.definition
      // Include ALL actions in bot.definition.ts (even hidden ones)
      actionDefs[def.name] = {
        title: def.title,
        description: def.description,
        attributes: def.attributes,
        input: def.input,
        output: def.output,
        cached: def.cached,
      }
    }

    const toEventName = (ename: string) => ename.replaceAll(/[^a-zA-Z0-9]/g, '').toLowerCase()

    const content = dedent`
      import { BotDefinition, z } from "@botpress/sdk";
      import {
      BUILT_IN_STATES,
      TranscriptSchema,
      TrackedStateSchema,
      WorkflowCallbackEvent,
      WorkflowScheduleEvent,
      WorkflowContinueEvent,
      SubworkflowFinished,
      WorkflowDataRequestEvent,
    } from "@botpress/runtime/definition";
      ${imports.length > 0 ? '\n' + imports.join('\n') : ''}

      const bot = new BotDefinition({
        conversation: {
          tags: {
            ${Object.entries(conversationTags)
              .map(
                ([tag, meta]) =>
                  `// ${meta.title}\n            // ${meta.description}\n            "${tag}": ${JSON.stringify(meta)}`
              )
              .join(',\n            ')}
          },
        },
        ${
          Object.keys(workflowTags).length > 0
            ? `workflow: {
          tags: {
            ${Object.entries(workflowTags)
              .map(
                ([tag, meta]) =>
                  `// ${meta.title}\n            // ${meta.description}\n            "${tag}": ${JSON.stringify(meta)}`
              )
              .join(',\n            ')}
          },
        },`
            : ''
        }
        ${
          workflowDefs.length > 0
            ? `workflows: {
          ${workflowDefs.join(',\n          ')}
        },`
            : ''
        }
        ${
          Object.keys(actionDefs).length > 0
            ? `actions: {
          ${Object.entries(actionDefs)
            .map(([name, def]) => {
              const parts = []
              if (def.title) parts.push(`title: ${JSON.stringify(def.title)}`)
              if (def.description) parts.push(`description: ${JSON.stringify(def.description)}`)
              if (def.attributes) parts.push(`attributes: ${JSON.stringify(def.attributes)}`)
              parts.push(`input: { schema: ${transforms.fromJSONSchema(def.input).toTypescriptSchema()} }`)
              parts.push(`output: { schema: ${transforms.fromJSONSchema(def.output).toTypescriptSchema()} }`)
              return `"${name}": {\n            ${parts.join(',\n            ')}\n          }`
            })
            .join(',\n          ')}
        },`
            : ''
        }
        ${
          recurringWorkflows.length > 0
            ? `recurringEvents: {
          ${recurringWorkflows
            .map(
              (wf) => `"${toEventName(wf.name + 'Schedule')}": {
            type: WorkflowScheduleEvent.name,
            schedule: { cron: "${wf.schedule}" },
            payload: { workflow: "${wf.name}" },
          }`
            )
            .join(',\n          ')}
        },`
            : ''
        }

        events: {
          [WorkflowScheduleEvent.name]: {
            schema: WorkflowScheduleEvent.schema,
          },
          [WorkflowCallbackEvent.name]: {
            schema: WorkflowCallbackEvent.schema,
          },
          [WorkflowContinueEvent.name]: {
            schema: WorkflowContinueEvent.schema,
          },
          [SubworkflowFinished.name]: {
            schema: SubworkflowFinished.schema,
          },
          [WorkflowDataRequestEvent.name]: {
            schema: WorkflowDataRequestEvent.schema,
          },
        },

        states: {
          /**
           * This is the ADK-native conversation state that contains the
           * necessary data to run the conversation and its handlers.
          */
          conversation: {
            type: "conversation",
            schema: z.object({ transcript: TranscriptSchema }),
          },

          /**
           * This is a generic state to store the conversation-specific state.
           * This is defined by the users at build-time when they define conversations.
           * Because each conversation can have its own state schema, we use \`z.any()\`
          */
          [BUILT_IN_STATES.conversation]: {
            type: "conversation",
            schema: TrackedStateSchema,
          },

          /**
           * Bot-wide global state that persists across all conversations
           */
          [BUILT_IN_STATES.bot]: {
            type: "bot",
            schema: TrackedStateSchema,
          },

          /**
           * User-specific state that persists across conversations for each user
           */
          [BUILT_IN_STATES.user]: {
            type: "user",
            schema: TrackedStateSchema,
          },

          /**
           * Workflow-specific state that persists across workflow executions
           */
          [BUILT_IN_STATES.workflowState]: {
            type: "workflow",
            schema: TrackedStateSchema,
          },

          /**
           * Workflow cached steps executions
           */
          [BUILT_IN_STATES.workflowSteps]: {
            type: "workflow",
            schema: TrackedStateSchema,
          },
        },
      });

      ${addIntegrations.length > 0 ? '\n' + addIntegrations.join('\n') : ''}


      export default bot;
    `

    await createFile(path.join(this.outputPath, 'bot.definition.ts'), await formatCode(content))
  }

  private async generateBotIndex(): Promise<void> {
    const content = dedent`
      import * as bp from '.botpress'
      import { setupAdkRuntime } from './adk-runtime'
      import { Actions } from './actions'
      import {isMainThread, isWorkerMode, initializeParentWorker, runWorker, BuiltInActions} from '@botpress/runtime/internal'

      const bot = new bp.Bot({
        actions: {}
      })

      // ============================================================================
      // WORKER INITIALIZATION
      // ============================================================================

      if (isWorkerMode() && isMainThread) {
        // Branch 1: Main thread in worker mode - initialize parent with pool
        if (process.env.BP_DEBUG) console.log("[Main] Initializing parent worker with pool...");
        initializeParentWorker(bot);
      } else if (isWorkerMode() && process.env.IS_DEV_WORKER === "true") {
        // Branch 2: Worker thread - run child worker
        if (process.env.BP_DEBUG) console.log("[Worker] Initializing child worker...");
        runWorker(bot);
        setupAdkRuntime(bot);
      } else {
        // Branch 3: Worker mode disabled - single-thread mode
        if (process.env.BP_DEBUG) console.log("[Bot] Running in single-thread mode");
        setupAdkRuntime(bot);
      }

      export default bot
    `

    await createFile(path.join(this.outputPath, 'src', 'index.ts'), await formatCode(content))
  }

  private async generatePackageJson(project: AgentProject): Promise<void> {
    const packageJson = {
      name: `@bp-templates/${project.config?.name || 'agent'}-bot`,
      scripts: {
        'check:type': 'tsc --noEmit',
      },
      private: true,
      dependencies: {
        '@botpress/client': '1.24.2',
        '@botpress/sdk': '4.15.7',
        '@botpress/runtime': 'workspace:*',
      },
      devDependencies: {
        '@types/node': '^22.16.4',
        typescript: '^5.9.3',
      },
    }

    await createFile(path.join(this.outputPath, 'package.json'), JSON.stringify(packageJson, null, 2))
  }

  private async generateTsConfig(): Promise<void> {
    const tsConfig = {
      compilerOptions: {
        lib: ['es2022'],
        module: 'es2022',
        strict: true,
        esModuleInterop: true,
        skipLibCheck: true,
        forceConsistentCasingInFileNames: true,
        moduleResolution: 'bundler',
        allowUnusedLabels: false,
        allowUnreachableCode: false,
        noFallthroughCasesInSwitch: true,
        noImplicitOverride: true,
        noImplicitReturns: true,
        noUncheckedIndexedAccess: true,
        noUnusedParameters: true,
        target: 'es2017',
        baseUrl: '.',
        outDir: 'dist',
        checkJs: false,
        incremental: true,
        exactOptionalPropertyTypes: false,
        resolveJsonModule: true,
        noPropertyAccessFromIndexSignature: false,
        noUnusedLocals: false,
      },
      include: ['.botpress/**/*', 'src/**/*', 'bp_modules/**/*', './*.ts', './*.json'],
    }

    await createFile(path.join(this.outputPath, 'tsconfig.json'), JSON.stringify(tsConfig, null, 2))
  }

  private async generateGlobalTypes(): Promise<void> {
    const content = dedent`
      // Global types for ADK assets
      declare global {
        const assets: {
          get(path: string): Promise<{
            url: string;
            path: string;
            name: string;
            size: number;
            mime: string;
            hash: string;
            createdAt: string;
            updatedAt: string;
            fileId: string;
          }>;
        };
      }

      export {};
    `

    await createFile(path.join(this.outputPath, 'global.d.ts'), await formatCode(content))
  }

  private async copyAssets(): Promise<void> {
    const assetsPath = path.join(this.projectPath, 'assets')
    const targetPath = path.join(this.outputPath, 'assets')

    if (existsSync(assetsPath)) {
      await fs.mkdir(targetPath, { recursive: true })
      // Copy assets recursively
      await this.copyDirectory(assetsPath, targetPath)
    }
  }

  private async copyDirectory(src: string, dest: string): Promise<void> {
    const entries = await fs.readdir(src, { withFileTypes: true })

    for (const entry of entries) {
      const srcPath = path.join(src, entry.name)
      const destPath = path.join(dest, entry.name)

      if (entry.isDirectory()) {
        await fs.mkdir(destPath, { recursive: true })
        await this.copyDirectory(srcPath, destPath)
      } else {
        await fs.copyFile(srcPath, destPath)
      }
    }
  }

  async generateAdkRuntime(): Promise<void> {
    const project = new AgentProject(this.projectPath)
    await project.reload()

    const srcDir = path.join(this.outputPath, 'src')

    /* <conversations.ts> */ {
      const dest = path.join(srcDir, 'conversations.ts')
      const imports = new Map<
        string,
        {
          statement: string
          name: string
        }
      >()
      const exports = new Set<string>()
      let index = 1

      for (const conversation of project.conversations) {
        if (!imports.has(conversation.path)) {
          const name = `conversations_${index++}`
          const importPath = getImportPath(dest, path.join(project.path, conversation.path))

          imports.set(conversation.path, {
            name,
            statement: `import * as ${name} from "${importPath}";`,
          })
        }

        exports.add(
          `"${conversation.definition.channel}": ${imports.get(conversation.path)!.name}.${conversation.export}`
        )
      }

      const content = `
      ////////////////////////////////////////////////////////
      // DO NOT EDIT THIS FILE DIRECTLY
      // This file is auto-generated from the Botpress ADK
      // File: conversations.ts
      ////////////////////////////////////////////////////////

      ${Array.from(imports)
        .map(([, { statement }]) => `${statement}`)
        .join('\n')}

      export const Conversations = {
        ${Array.from(exports).join(',\n')}
      };
      `

      await createFile(dest, await formatCode(content))
    } /* </conversations.ts> */

    /* <knowledge.ts> */ {
      const dest = path.join(srcDir, 'knowledge.ts')
      const imports = new Map<
        string,
        {
          statement: string
          name: string
        }
      >()
      const exports = new Set<string>()
      let index = 1

      for (const knowledge of project.knowledge) {
        if (!imports.has(knowledge.path)) {
          const name = `knowledge_${index++}`
          const importPath = getImportPath(dest, path.join(project.path, knowledge.path))

          imports.set(knowledge.path, {
            name,
            statement: `import * as ${name} from "${importPath}";`,
          })
        }

        exports.add(`"${knowledge.definition.name}": ${imports.get(knowledge.path)!.name}.${knowledge.export}`)
      }

      const content = `
      ////////////////////////////////////////////////////////
      // DO NOT EDIT THIS FILE DIRECTLY
      // This file is auto-generated from the Botpress ADK
      // File: knowledge.ts
      ////////////////////////////////////////////////////////

      ${Array.from(imports)
        .map(([, { statement }]) => `${statement}`)
        .join('\n')}

      export const Knowledge = {
        ${Array.from(exports).join(',\n')}
      };
      `

      await createFile(dest, await formatCode(content))
    } /* </knowledge.ts> */

    /* <triggers.ts> */ {
      const dest = path.join(srcDir, 'triggers.ts')
      const { transforms } = await import('@botpress/sdk')
      const imports = new Map<
        string,
        {
          statement: string
          name: string
        }
      >()
      const exports = new Set<string>()
      const payloadTypes: Record<string, string> = {}
      let index = 1

      // First pass: collect imports and exports
      for (const trigger of project.triggers) {
        if (!imports.has(trigger.path)) {
          const name = `triggers_${index++}`
          const importPath = getImportPath(dest, path.join(project.path, trigger.path))

          imports.set(trigger.path, {
            name,
            statement: `import * as ${name} from "${importPath}";`,
          })
        }

        exports.add(`"${trigger.definition.name}": ${imports.get(trigger.path)!.name}.${trigger.export}`)
      }

      // Second pass: extract payload schemas and compile to TypeScript types
      for (const trigger of project.triggers) {
        try {
          // Import the trigger module to get the actual instance
          const absolutePath = path.join(project.path, trigger.path)
          // Bust module cache to ensure fresh trigger on regeneration
          const triggerModule = await import(`${absolutePath}?t=${Date.now()}`)
          const triggerInstance = triggerModule[trigger.export] || triggerModule.default

          if (triggerInstance && triggerInstance.payload) {
            // Convert Zod schema to JSON Schema, then to TypeScript type
            const jsonSchema = transforms.toJSONSchema(triggerInstance.payload)
            const payloadType = transforms.fromJSONSchema(jsonSchema).toTypescriptType()
            payloadTypes[trigger.definition.name] = payloadType
          } else {
            payloadTypes[trigger.definition.name] = '{}'
          }
        } catch (error) {
          console.warn(`Warning: Could not process trigger ${trigger.definition.name}:`, error)
          payloadTypes[trigger.definition.name] = '{}'
        }
      }

      const content = `
      ////////////////////////////////////////////////////////
      // DO NOT EDIT THIS FILE DIRECTLY
      // This file is auto-generated from the Botpress ADK
      // File: triggers.ts
      ////////////////////////////////////////////////////////

      ${Array.from(imports)
        .map(([, { statement }]) => `${statement}`)
        .join('\n')}

      export const Triggers = {
        ${Array.from(exports).join(',\n')}
      };

      // Extract trigger payload types with compiled TypeScript types
      type _TriggerPayloads = {
        ${Object.entries(payloadTypes)
          .map(([name, type]) => `"${name}": ${type}`)
          .join(';\n        ')}
      };

      declare global {
        export type TriggerPayloads = _TriggerPayloads;
      }
      `

      await createFile(dest, await formatCode(content))
    } /* </triggers.ts> */

    /* <workflows.ts> */ {
      const dest = path.join(srcDir, 'workflows.ts')
      const imports = new Map<
        string,
        {
          statement: string
          name: string
        }
      >()
      const exports = new Set<string>()
      let index = 1

      for (const workflow of project.workflows) {
        if (isBuiltinWorkflow(workflow.definition.name)) {
          // Built-in workflow - already handled in runtime
          continue
        }

        if (!imports.has(workflow.path)) {
          // User-defined workflow - import from project
          const name = `workflows_${index++}`
          const importPath = getImportPath(dest, path.join(project.path, workflow.path))

          const statement = `import * as ${name} from "${importPath}";`
          imports.set(workflow.path, {
            name,
            statement,
          })
        }
      }

      // Generate exports
      for (const workflow of project.workflows) {
        if (isBuiltinWorkflow(workflow.definition.name)) {
          // Built-in workflow - already handled in runtime
          continue
        }

        const importEntry = imports.get(workflow.path)!
        const exportStatement = workflow.export.replace('].', ']!.')

        exports.add(`"${workflow.definition.name}": ${importEntry.name}.${exportStatement}`)
      }

      const content = `
      ////////////////////////////////////////////////////////
      // DO NOT EDIT THIS FILE DIRECTLY
      // This file is auto-generated from the Botpress ADK
      // File: workflows.ts
      ////////////////////////////////////////////////////////

      ${Array.from(imports)
        .map(([, { statement }]) => `${statement}`)
        .join('\n')}

      export const Workflows = {
        ${Array.from(exports).join(',\n')}
      };
      `

      await createFile(dest, await formatCode(content))
    } /* </workflows.ts> */

    /* <actions.ts> */ {
      const dest = path.join(srcDir, 'actions.ts')
      const imports = new Map<
        string,
        {
          statement: string
          name: string
        }
      >()
      const exports = new Set<string>()
      let index = 1

      for (const action of project.actions) {
        if (isBuiltinAction(action.definition.name)) {
          // Built-in action - already handled in runtime
          continue
        }

        if (!imports.has(action.path)) {
          const name = `actions_${index++}`
          const importPath = getImportPath(dest, path.join(project.path, action.path))

          imports.set(action.path, {
            name,
            statement: `import * as ${name} from "${importPath}";`,
          })
        }

        exports.add(`"${action.definition.name}": ${imports.get(action.path)!.name}.${action.export}`)
      }

      const content = `
      ////////////////////////////////////////////////////////
      // DO NOT EDIT THIS FILE DIRECTLY
      // This file is auto-generated from the Botpress ADK
      // File: actions.ts
      ////////////////////////////////////////////////////////

      ${Array.from(imports)
        .map(([, { statement }]) => `${statement}`)
        .join('\n')}

      export const Actions = {
        ${Array.from(exports).join(',\n')}
      };
      `

      await createFile(dest, await formatCode(content))
    } /* </actions.ts> */

    /* <tables.ts> */ {
      const dest = path.join(srcDir, 'tables.ts')
      const imports = new Map<
        string,
        {
          statement: string
          name: string
        }
      >()
      const exports = new Set<string>()
      let index = 1

      for (const table of project.tables) {
        if (!imports.has(table.path)) {
          const name = `tables_${index++}`
          const importPath = getImportPath(dest, path.join(project.path, table.path))

          imports.set(table.path, {
            name,
            statement: `import * as ${name} from "${importPath}";`,
          })
        }

        exports.add(`"${table.definition.name}": ${imports.get(table.path)!.name}.${table.export}`)
      }

      const content = `
      ////////////////////////////////////////////////////////
      // DO NOT EDIT THIS FILE DIRECTLY
      // This file is auto-generated from the Botpress ADK
      // File: tables.ts
      ////////////////////////////////////////////////////////

      ${Array.from(imports)
        .map(([, { statement }]) => `${statement}`)
        .join('\n')}

      export const Tables = {
        ${Array.from(exports).join(',\n')}
      };
      `

      await createFile(dest, await formatCode(content))
    } /* </tables.ts> */

    /* <config.ts> */ {
      const dest = path.join(srcDir, 'config.ts')
      const importPath = getImportPath(dest, path.join(project.path, 'agent.config.ts'))

      const content = `
      ////////////////////////////////////////////////////////
      // DO NOT EDIT THIS FILE DIRECTLY
      // This file is auto-generated from the Botpress ADK
      // File: config.ts
      ////////////////////////////////////////////////////////

      import AgentConfigImport from "${importPath}";

      export const AgentConfig = AgentConfigImport;
      `

      await createFile(dest, await formatCode(content))
    } /* </config.ts> */

    const content = dedent`
      import * as bp from ".botpress";
      import { IntegrationDefinitions } from "./integrations";
      import { InterfaceDefinitions } from "./interfaces";
      import { initializeAssets } from "./assets-runtime";
      import { handlers, patchHandlers, agentRegistry, z, initialize, register, registerIntegration } from "@botpress/runtime/runtime";
      import { AgentConfig } from "./config";
      import { Conversations } from "./conversations";
      import { Knowledge } from "./knowledge";
      import { Triggers } from "./triggers";
      import { Workflows } from "./workflows";
      import { Actions } from "./actions";
      import { Tables } from "./tables";
      import { Interfaces } from "../../interfaces";

      export function setupAdkRuntime(bot: bp.Bot) {
        // Initialize global error handlers for the runtime

        initialize({ config: AgentConfig })

        register(...Object.values(Conversations));
        register(...Object.values(Workflows));
        register(...Object.values(Triggers));
        register(...Object.values(Actions));
        register(...Object.values(Tables));
        register(...Object.values(Knowledge));

        for (const [alias, { definition }] of Object.entries(IntegrationDefinitions)) {
          registerIntegration({ alias, definition });
        }

        // Initialize the global agent registry
        agentRegistry.initialize({
          integrations: Object.entries(IntegrationDefinitions).map(([alias, def]) => ({ ...def, alias })),
          interfaces: Object.entries(InterfaceDefinitions).map(([alias, def]) => ({ ...def, alias })),
          interfacesMapping: Interfaces as Record<string, any>
        });

        // Patch bot handlers to add runtime context
        patchHandlers(bot);

        // Initialize assets system
        initializeAssets();

        // Setup conversation, trigger, and workflow handlers
        handlers.conversation.setup(bot);
        handlers.event.setup(bot);
        handlers.trigger.setup(bot);
        handlers.workflow.setup(bot);
        handlers.actions.setup(bot);
      }
    `

    await createFile(path.join(this.outputPath, 'src', 'adk-runtime.ts'), await formatCode(content))
  }

  async copyAssetsRuntime(): Promise<void> {
    const assetsRuntimePath = path.join(this.projectPath, '.adk', 'assets-runtime.ts')
    if (existsSync(assetsRuntimePath)) {
      const content = await fs.readFile(assetsRuntimePath, 'utf-8')
      await createFile(path.join(this.outputPath, 'src', 'assets-runtime.ts'), await formatCode(content))
    }
  }
}

export async function generateBotProject(options: BotGeneratorOptions): Promise<void> {
  const generator = new BotGenerator(options)
  await generator.generate()
  await generator.generateAdkRuntime()
  await generator.copyAssetsRuntime()

  // Handle devId/botId management
  const devIdManager = new DevIdManager(
    options.projectPath,
    options.outputPath || path.join(options.projectPath, '.adk', 'bot')
  )

  // Restore any saved devId/botId from agent.json to project cache
  await devIdManager.restoreDevId()

  // Sync integrations
  const integrationSync = new IntegrationSync(
    options.projectPath,
    options.outputPath || path.join(options.projectPath, '.adk', 'bot')
  )
  const integrationSyncResult = await integrationSync.syncIntegrations()

  if (integrationSyncResult.errors.length > 0) {
    console.warn(`⚠️  Some integrations failed to sync:`)
    integrationSyncResult.errors.forEach(({ alias, error }) => {
      console.warn(`  - ${alias}: ${error}`)
    })
  }

  // Sync interfaces
  const interfaceSync = new InterfaceSync(
    options.projectPath,
    options.outputPath || path.join(options.projectPath, '.adk', 'bot')
  )
  const interfaceSyncResult = await interfaceSync.syncInterfaces()

  if (interfaceSyncResult.errors.length > 0) {
    console.warn(`⚠️  Some interfaces failed to sync:`)
    interfaceSyncResult.errors.forEach(({ alias, error }) => {
      console.warn(`  - ${alias}: ${error}`)
    })
  }
}
